import { useCallback, useRef, useState } from "react";

// Колко стъпки назад се помнят — повече няма смисъл за ръчна подредба.
const HISTORY_LIMIT = 50;

export interface HistoryEntry {
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

/**
 * Отмени / Повтори на редактора. Всяка стъпка е двойка заявки към BE (обратната
 * и правата). Изтрит и възстановен елемент получава НОВО id от базата, затова
 * стъпките пазят id-то от момента на записа и го превеждат през `resolveId`
 * (`aliases`: старо id → ново), когато се изпълнят.
 */
export const useSchemaHistory = () => {
  const [past, setPast] = useState<HistoryEntry[]>([]);
  const [future, setFuture] = useState<HistoryEntry[]>([]);
  const [isReplaying, setIsReplaying] = useState(false);
  const aliasesRef = useRef(new Map<number, number>());

  // Псевдонимите са винаги „направо към най-новото id" (веригата се свива при
  // запис), затова превеждането е една стъпка при всеки брой отмени/повтори.
  const resolveId = useCallback(
    (elementId: number) => aliasesRef.current.get(elementId) ?? elementId,
    [],
  );

  const recordAlias = useCallback((previousId: number, nextId: number) => {
    if (previousId === nextId) return;
    const aliases = aliasesRef.current;
    for (const [oldId, currentId] of aliases) {
      if (currentId === previousId) aliases.set(oldId, nextId);
    }
    aliases.set(previousId, nextId);
  }, []);

  /** Нов регион → нова история: ⌘Z не бива да пипа невидим регион. */
  const reset = useCallback(() => {
    setPast([]);
    setFuture([]);
  }, []);

  const record = useCallback((entry: HistoryEntry) => {
    setPast((entries) => [...entries.slice(-(HISTORY_LIMIT - 1)), entry]);
    setFuture([]);
  }, []);

  const replay = useCallback(
    async (direction: "undo" | "redo") => {
      if (isReplaying) return;
      const source = direction === "undo" ? past : future;
      const entry = source[source.length - 1];
      if (!entry) return;
      setIsReplaying(true);
      const setSource = direction === "undo" ? setPast : setFuture;
      const setTarget = direction === "undo" ? setFuture : setPast;
      setSource((entries) => entries.slice(0, -1));
      try {
        await entry[direction]();
        setTarget((entries) => [...entries, entry]);
      } catch {
        // Стъпката не мина (грешката е показана от действието) — не се връща в
        // нито един стек, иначе следващото ⌘Z ще удари същата стена.
      } finally {
        setIsReplaying(false);
      }
    },
    [isReplaying, past, future],
  );

  return {
    record,
    reset,
    resolveId,
    recordAlias,
    undo: () => replay("undo"),
    redo: () => replay("redo"),
    canUndo: past.length > 0 && !isReplaying,
    canRedo: future.length > 0 && !isReplaying,
  };
};

export type SchemaHistory = ReturnType<typeof useSchemaHistory>;
